import React from 'react'
import { useTranslation } from 'react-i18next'
import UiButton, { UiButtonSize, UiButtonStyle } from 'components/Ui/UiButton'
import LeadTagsAutocomplete from 'components/leads/forms/LeadTagsAutocomplete'
import {toast} from 'utils/toastUtils'

interface ExportTagsModalProps {
    title: string
    message?: string | React.ReactNode
    defaultTags?: string[]
    confirmLabel?: string
    cancelLabel?: string
    placeholder?: string
    onConfirm: (tags: string[]) => void | Promise<void>
    onCancel: () => void
}

const ExportTagsModal: React.FC<ExportTagsModalProps> = ({
    title,
    message,
    defaultTags = [],
    confirmLabel,
    cancelLabel,
    placeholder,
    onConfirm,
    onCancel,
}) => {
    const { t } = useTranslation()
    const [tags, setTags] = React.useState<string[]>(defaultTags)
    const [isSubmitting, setIsSubmitting] = React.useState(false)

    const handleConfirm = () => {
        if (isSubmitting) {
            return
        }

        try {
            const result = onConfirm(tags)
            if (result && typeof (result as Promise<void>).then === 'function') {
                setIsSubmitting(true)
                ;(result as Promise<void>)
                    .catch(() => toast.error(t('Export failed')))
                    .finally(() => setIsSubmitting(false))
            }
        } catch {
            toast.error(t('Export failed'))
        }
    }

    return (
        <div className="ExportTagsModal p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{title}</h3>
            {message && (
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">{message}</div>
            )}

            <div className="mb-3">
                <div className="mb-2 text-sm font-medium text-gray-800 dark:text-gray-200">
                    {t('Add export tags')}
                </div>
                <LeadTagsAutocomplete
                    selectedTags={tags}
                    onTagsChange={setTags}
                    placeholder={placeholder || (t('Type to search or add tags...') as string)}
                />
            </div>

            <div className="flex justify-end gap-3">
                <UiButton
                    style={UiButtonStyle.Secondary}
                    size={UiButtonSize.Medium}
                    onClick={onCancel}
                    disabled={isSubmitting}
                >
                    {cancelLabel || t('Cancel')}
                </UiButton>
                <UiButton
                    style={UiButtonStyle.Primary}
                    size={UiButtonSize.Medium}
                    onClick={handleConfirm}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                >
                    {confirmLabel || t('Confirm')}
                </UiButton>
            </div>
        </div>
    )
}

export default ExportTagsModal
